import { createStore } from 'zustand/vanilla';
import { omitIdTokens, prunePersistedSession, resolveExpiry } from './cache';
import { makeScopeKey, normalizeScope, parseScopeKey } from './scope';
import * as tokenStorage from './tokenStorage';
import type {
	AcquisitionIntent,
	AuthScope,
	AuthSession,
	CredentialEntry,
	PersistedAuthSessionV2,
	ScopeKey,
	ScopedCredential,
} from './types';

interface ScopedSessionState {
	entries: Record<ScopeKey, CredentialEntry>;
	activeScopeKey: ScopeKey | null;
	lastUsableScopeKey: ScopeKey | null;
	loginEpoch: number;
	nextRevision: number;
}

export interface SessionStoreState extends AuthSession, ScopedSessionState {
	set: (session: Partial<AuthSession>) => void;
	hydrate: () => void;
	bootstrapLogin: (
		scope: AuthScope,
		credential: ScopedCredential,
		isSuperUser: boolean,
		isStaff?: boolean,
	) => void;
	findUsableEntry: (scope: AuthScope, now?: number) => CredentialEntry | null;
	activateScope: (scope: AuthScope, now?: number) => boolean;
	beginAcquisition: (scope: AuthScope) => AcquisitionIntent;
	saveAcquiredCredential: (intent: AcquisitionIntent, credential: ScopedCredential) => boolean;
	compareAndSetCredential: (
		scopeKey: ScopeKey,
		revision: number,
		credential: ScopedCredential,
	) => boolean;
	evictScope: (scopeKey: ScopeKey) => void;
	invalidateLoginEpoch: () => void;
	// Re-writes current state to storage, after a legacy wholesale
	// localStorage.clear() would otherwise erase a just-committed session.
	persistToStorage: () => void;
	clear: () => void;
}

const DEFAULT_SESSION: AuthSession & ScopedSessionState = {
	accessToken: null,
	refreshToken: null,
	idToken: null,
	isSuperUser: false,
	isStaff: false,
	defaultCompanyId: null,
	delegationsIds: null,
	entries: {},
	activeScopeKey: null,
	lastUsableScopeKey: null,
	loginEpoch: 0,
	nextRevision: 1,
};

const getSessionDelegationIds = (scope: AuthScope): number[] | null =>
	scope.delegationIds.length === 0 ? null : [...scope.delegationIds];

const toPersisted = (state: SessionStoreState, now: number): PersistedAuthSessionV2 =>
	prunePersistedSession(
		{
			version: 2,
			loginEpoch: state.loginEpoch,
			nextRevision: state.nextRevision,
			isSuperUser: state.isSuperUser,
			isStaff: state.isStaff,
			activeScopeKey: state.activeScopeKey,
			lastUsableScopeKey: state.lastUsableScopeKey,
			entries: omitIdTokens(state.entries, now),
		},
		now,
	)!;

// Restores every persisted entry, not just the active scope's. Only the active
// one is ever read for authentication (`activate` drives the flat token fields);
// the rest come back as the per-scope record of accesses this session made —
// which is why a non-active entry being restored is NOT the same as it being
// trusted. See the caveat on `saveAcquiredCredential`.
const toState = (session: PersistedAuthSessionV2): Partial<SessionStoreState> => {
	const entries = Object.fromEntries(
		Object.entries(session.entries).flatMap(([key, persisted]) => {
			const scope = normalizeScope(persisted.companyId, persisted.delegationIds);
			if (scope === null) return [];
			const entry: CredentialEntry = { ...persisted, idToken: null, scope };
			return [[key as ScopeKey, entry] as const];
		}),
	) as Record<ScopeKey, CredentialEntry>;
	const activeScopeKey =
		session.activeScopeKey && entries[session.activeScopeKey] ? session.activeScopeKey : null;
	const activeEntry = activeScopeKey ? entries[activeScopeKey] : undefined;
	return {
		entries,
		activeScopeKey,
		lastUsableScopeKey:
			session.lastUsableScopeKey && entries[session.lastUsableScopeKey]
				? session.lastUsableScopeKey
				: activeScopeKey,
		loginEpoch: session.loginEpoch,
		nextRevision: session.nextRevision,
		isSuperUser: session.isSuperUser,
		isStaff: session.isStaff,
		accessToken: activeEntry?.accessToken ?? null,
		refreshToken: activeEntry?.refreshToken ?? null,
		idToken: null,
		defaultCompanyId: activeEntry?.scope.companyId ?? null,
		delegationsIds: activeEntry ? getSessionDelegationIds(activeEntry.scope) : null,
	};
};

export const sessionStore = createStore<SessionStoreState>((set, get) => {
	const persist = (): void => tokenStorage.writeV2(toPersisted(get(), Date.now()));
	const activate = (scopeKey: ScopeKey, now: number): boolean => {
		const entry = get().entries[scopeKey];
		if (!entry || entry.expiresAt <= now) return false;
		const updatedEntry = { ...entry, lastUsedAt: now };
		set({
			entries: { ...get().entries, [scopeKey]: updatedEntry },
			activeScopeKey: scopeKey,
			lastUsableScopeKey: scopeKey,
			accessToken: updatedEntry.accessToken,
			refreshToken: updatedEntry.refreshToken,
			idToken: updatedEntry.idToken,
			defaultCompanyId: updatedEntry.scope.companyId,
			delegationsIds: getSessionDelegationIds(updatedEntry.scope),
		});
		persist();
		return true;
	};

	return {
		...DEFAULT_SESSION,
		// The flat-session writer behind `setAuthSession`, reached only by the login
		// paths that hand over a whole `AuthSession` (`useLoginBetaUsersMutation`,
		// `useLogin`'s no-company superuser branch). It REPLACES the entry map for
		// the same reason `bootstrapLogin` resets it: a login is a new session, not
		// another access within one. Scope moves accumulate via
		// `saveAcquiredCredential` instead.
		set: (session) => {
			const nextSession = { ...get(), ...session };
			const scope = normalizeScope(nextSession.defaultCompanyId, nextSession.delegationsIds);

			if (nextSession.accessToken !== null && nextSession.refreshToken !== null && scope !== null) {
				const now = Date.now();
				const scopeKey = makeScopeKey(scope);
				const entry: CredentialEntry = {
					accessToken: nextSession.accessToken,
					refreshToken: nextSession.refreshToken,
					idToken: nextSession.idToken,
					scope,
					revision: get().nextRevision,
					acquiredAt: now,
					lastUsedAt: now,
					expiresAt: resolveExpiry(nextSession.refreshToken, now),
				};
				set({
					...session,
					entries: { [scopeKey]: entry },
					activeScopeKey: scopeKey,
					lastUsableScopeKey: scopeKey,
					nextRevision: entry.revision + 1,
					delegationsIds: getSessionDelegationIds(scope),
				});
				persist();
				return;
			}

			set(session);
			persist();
		},
		hydrate: () => {
			const session = tokenStorage.readV2();
			set(session ? toState(session) : { ...DEFAULT_SESSION, loginEpoch: get().loginEpoch });
		},
		bootstrapLogin: (scope, credential, isSuperUser, isStaff = false) => {
			// A login starts the entry map over. `saveAcquiredCredential` accumulates,
			// and `clearAppBeforeLogin` (useLogin) only wipes localStorage — without
			// this reset the entries of whoever was signed in a moment ago would ride
			// into the new session in memory, keyed by scopes the new user may not
			// even have access to.
			set({ entries: {} });
			const intent = get().beginAcquisition(scope);
			set({ isSuperUser, isStaff });
			get().saveAcquiredCredential(intent, credential);
		},
		findUsableEntry: (scope, now = Date.now()) => {
			const entry = get().entries[makeScopeKey(scope)];
			return entry && entry.expiresAt > now ? entry : null;
		},
		activateScope: (scope, now = Date.now()) => activate(makeScopeKey(scope), now),
		beginAcquisition: (scope) => ({
			targetScopeKey: makeScopeKey(scope),
			intentRevision: get().nextRevision,
			loginEpoch: get().loginEpoch,
		}),
		// Committing a credential ADDS an entry, keyed by scope, and leaves the
		// previously visited scopes' entries in place: one entry per
		// company/delegation combination this session has accessed, each holding the
		// credential that was issued for it.
		//
		// Caveat, deliberately kept: the backend keeps a single credential alive per
		// user — issuing one (`/login`, `/login/impersonate/:id`,
		// `/login/switch-credentials` alike) invalidates the one it replaces and
		// rejects it as `auth.token.rejected` / "token expirado" long before the
		// `exp` its own JWT advertises (evidence in the Rule in
		// context/auth/README.md). So every entry but the active one may already name
		// a dead credential. That is why nothing reads a non-active entry to
		// authenticate with: `transitionAuthScope` re-acquires on every scope change
		// instead of looking the target up here, and `refreshAuthSession` only ever
		// refreshes the scope a request was actually bound to. Reading one of these
		// entries as a shortcut back into its scope is what reintroduces the 401
		// cascade.
		saveAcquiredCredential: (intent, credential) => {
			if (intent.loginEpoch !== get().loginEpoch) return false;
			if (intent.intentRevision !== get().nextRevision) return false;
			const scope = parseScopeKey(intent.targetScopeKey);
			if (scope === null) return false;
			const now = Date.now();
			const entry: CredentialEntry = {
				...credential,
				scope,
				revision: get().nextRevision,
				acquiredAt: now,
				lastUsedAt: now,
				expiresAt: resolveExpiry(credential.refreshToken, now),
			};
			set({
				entries: { ...get().entries, [intent.targetScopeKey]: entry },
				nextRevision: entry.revision + 1,
			});
			return activate(intent.targetScopeKey, now);
		},
		compareAndSetCredential: (scopeKey, revision, credential) => {
			const previous = get().entries[scopeKey];
			if (!previous || previous.revision !== revision) return false;
			const now = Date.now();
			const entry: CredentialEntry = {
				...previous,
				...credential,
				revision: get().nextRevision,
				acquiredAt: now,
				lastUsedAt: now,
				expiresAt: resolveExpiry(credential.refreshToken, now),
			};
			set({ entries: { ...get().entries, [scopeKey]: entry }, nextRevision: entry.revision + 1 });
			if (get().activeScopeKey === scopeKey) return activate(scopeKey, now);
			persist();
			return true;
		},
		// A rejected refresh ends the session, full stop — even though entries for
		// other scopes now routinely survive in the map. They are a record of past
		// accesses, not a fallback: the backend had already revoked each of them when
		// it issued the credential that just got rejected. So the flat token fields
		// go null regardless, and `selectIsAuthenticated` cannot report an
		// already-revoked token as authenticated. The caller (the Axios response
		// interceptor) reads the resulting null snapshot and owns the app reset +
		// redirect. This used to activate an arbitrary surviving entry instead,
		// which silently dropped the user into a company they never picked, holding
		// a credential that was just as dead.
		evictScope: (scopeKey) => {
			const { [scopeKey]: _evicted, ...remaining } = get().entries;
			set({
				entries: remaining,
				activeScopeKey: null,
				lastUsableScopeKey: null,
				accessToken: null,
				refreshToken: null,
				idToken: null,
				defaultCompanyId: null,
				delegationsIds: null,
			});
			persist();
		},
		invalidateLoginEpoch: () => set({ loginEpoch: get().loginEpoch + 1 }),
		persistToStorage: () => persist(),
		clear: () => {
			const loginEpoch = get().loginEpoch + 1;
			set({ ...DEFAULT_SESSION, loginEpoch });
			tokenStorage.clear();
		},
	};
});
